'use client';

/**
 * EmptyStateSection — schema-driven explorer empty state.
 *
 * Renders icon + label + message from the section's static
 * `ExplorerSectionConfig`. When mounted under
 * `<ExplorerSearchProvider />` the section suppresses itself once any
 * sibling section is contributing content (project card, clarification,
 * search results) — that's the existing obeya-explorer rule:
 * "Empty State only shows when nothing else is on the screen."
 *
 * Without a provider the section is purely config-driven and always
 * renders — that's how filter-style modes (testing, etc.) get their
 * "no test cases found" hint.
 *
 * @see klyde_edge/ui_config/entity_schema.py — ExplorerSectionType.EMPTY_STATE
 *
 * @component_role: Section
 * @component_type: Section Renderer
 * @domain: Frontend / Explorer
 * @layer: presentation
 */

import { Bot, FlaskConical, MessageSquare, Package, Palette, Clock, FileText, Search } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ExplorerSectionConfig, LabelsDict } from '@/lib/api/types';
import { useExplorerSearch } from './explorer-search-context';

// Schema-named icons → Lucide components. Mirrors the icon names that
// already appear in EXPLORER_SCHEMA today; new schema icons land here
// alongside the schema entry that uses them.
const SECTION_ICONS: Record<string, LucideIcon> = {
  Bot,
  FlaskConical,
  MessageSquare,
  Package,
  Palette,
  Clock,
  FileText,
  Search,
};

interface EmptyStateSectionProps {
  config: ExplorerSectionConfig;
  labels: LabelsDict;
}

export function EmptyStateSection({ config, labels }: EmptyStateSectionProps) {
  const search = useExplorerSearch();

  // Suppress when a sibling section already has content. Provider owns
  // the predicate so the rule is in one place; sections without a
  // provider always render (the schema declared them, after all).
  if (search?.hasContent) return null;

  const Icon = config.icon ? SECTION_ICONS[config.icon] : null;
  // Translated strings preferred; schema literals are the fallback for
  // labels keys that haven't been added to the translation dict yet.
  const label = (config.label && labels[config.label]) || config.label;
  const message = (config.message && labels[config.message]) || config.message;
  const hint = (config.help_text && labels[config.help_text]) || config.help_text;

  return (
    <div className="flex flex-col items-center justify-center text-muted-foreground p-6 text-center">
      {Icon && <Icon className="w-12 h-12 mb-4 opacity-30" />}
      {label && <p className="text-sm font-medium">{label}</p>}
      {message && (
        <p className="text-xs mt-2 max-w-[200px]">{message}</p>
      )}
      {hint && (
        <p className="text-xs mt-4 text-muted-foreground/70">{hint}</p>
      )}
    </div>
  );
}
